import type { ContentStoryProps } from '@/features/common/ContentStory/types/ContentStoryProps';
import '@/features/common/ContentStory/content-page.css';

/**
 * A news item or article as the public site shows it on /stiri/{slug}: the cover with its category
 * on it (or the category alone over the title when there is no picture), the headline, the line
 * under it, and the text.
 *
 * <p>In common because two screens draw it and they must not drift: the story page itself, and the
 * approval queue, where the approver reads the item as it will look once published. The body is
 * inserted unescaped for the reason ContentPage gives — every body is stored through the backend's
 * HtmlSanitizer, and the workflow endpoint sanitizes the clipped text once more on the way out.
 */
export function ContentStory({
  title,
  titleTag: Title,
  category,
  coverSrc,
  onCoverError,
  bodyHtml,
  lang,
  leading,
  meta,
}: ContentStoryProps) {
  return (
    <article className="content-page content-page--story">
      {leading}
      {coverSrc === null ? (
        <span className="cover-chip cover-chip--static">{category}</span>
      ) : (
        <div className="content-page__media">
          <img
            className="content-page__cover"
            src={coverSrc}
            alt=""
            aria-hidden="true"
            onError={onCoverError}
          />
          <span className="cover-chip">{category}</span>
        </div>
      )}
      <Title className="content-page__title" lang={lang}>
        {title}
      </Title>
      {meta && <p className="content-page__meta">{meta}</p>}
      {/* HTML SINK — source: the stored body its caller loaded (the public story, the review
          preview) · written by: any authenticated user, through the contribution endpoint ·
          sanitised by: the backend HtmlSanitizer, on the write path. This component is handed
          markup and says so in its prop name; it adds nothing to it. */}
      <div
        className="content-page__body"
        lang={lang}
        dangerouslySetInnerHTML={{ __html: bodyHtml }}
      />
    </article>
  );
}
